/**
 * API client for the human expert review feature (user-facing).
 *
 * @module humanReviewsApi
 */

import { apiGet, apiPost } from '@/lib/apiUtils';

export type ReviewModule = 'writing' | 'speaking';
export type ReviewStatus = 'pending' | 'in_progress' | 'completed' | 'cancelled';
export type ReviewPriority = 'standard' | 'priority';

export interface Entitlement {
  readonly includedCount: number;
  readonly consumedCount: number;
  readonly remaining: number;
  readonly expiresAt?: string | null;
  readonly planCode: string;
}

export interface ReviewResult {
  readonly verifiedBand: string;
  readonly readinessVerdict: string;
  readonly prioritizedFixes: string[];
  readonly attachedQuestionAnswer: string;
  readonly createdAt: string;
}

export interface ReviewListItem {
  readonly id: string;
  readonly module: ReviewModule;
  readonly status: ReviewStatus;
  readonly priority: ReviewPriority;
  readonly attachedQuestion: string;
  readonly hasResult: boolean;
  readonly createdAt: string;
  readonly completedAt?: string | null;
}

export interface ReviewDetail extends ReviewListItem {
  readonly notes?: string | null;
  readonly result?: ReviewResult | null;
  readonly contextText?: string | null;
  readonly questionMaterialText?: string | null;
  readonly questionText?: string | null;
  readonly answerText?: string | null;
  readonly audioUrl?: string | null;
  readonly aiNote?: string | null;
  readonly aiStrength?: string | null;
  readonly aiWeakness?: string | null;
  readonly aiGrammarFeedback?: string | null;
  readonly aiStructureFeedback?: string | null;
  readonly aiPronunciationFeedback?: string | null;
}

export interface ReviewSummary {
  readonly pendingCount: number;
  readonly inProgressCount: number;
  readonly completedCount: number;
  readonly totalCount: number;
}

export interface CreateReviewPayload {
  readonly ratingId?: string;
  readonly targetAnswerId: string;
  readonly module: ReviewModule;
  readonly attachedQuestion: string;
  readonly notes?: string;
}

export interface CreateReviewResponse {
  readonly id: string;
  readonly status: ReviewStatus;
  readonly remaining: number;
}

export interface CancelReviewResponse {
  readonly id: string;
  readonly status: ReviewStatus;
  readonly remaining: number;
}

export async function getEntitlement(): Promise<Entitlement | null> {
  return apiGet<Entitlement>('/human-reviews/entitlement', {
    errorMessage: 'Failed to load review quota',
    context: 'getEntitlement',
    suppressErrorToast: true,
  });
}

export async function getReviewSummary(): Promise<ReviewSummary | null> {
  return apiGet<ReviewSummary>('/human-reviews/summary', {
    errorMessage: 'Failed to load review summary',
    context: 'getReviewSummary',
    suppressErrorToast: true,
  });
}

export async function getReviewRequests(): Promise<ReviewListItem[]> {
  const result = await apiGet<ReviewListItem[]>('/human-reviews', {
    errorMessage: 'Failed to load your reviews',
    context: 'getReviewRequests',
  });
  return result ?? [];
}

export async function getReviewRequestDetail(id: string): Promise<ReviewDetail | null> {
  return apiGet<ReviewDetail>(`/human-reviews/${id}`, {
    errorMessage: 'Failed to load review',
    context: 'getReviewRequestDetail',
  });
}

/** Create a review request. Throws on error (incl. 409 no-quota) so the caller can branch. */
export async function createReviewRequest(
  payload: CreateReviewPayload,
): Promise<CreateReviewResponse | null> {
  return apiPost<CreateReviewResponse>('/human-reviews', payload, {
    errorMessage: 'Failed to submit review request',
    context: 'createReviewRequest',
    throwOnError: true,
  });
}

export async function cancelReviewRequest(id: string): Promise<CancelReviewResponse | null> {
  return apiPost<CancelReviewResponse>(`/human-reviews/${id}/cancel`, undefined, {
    errorMessage: 'Failed to cancel review',
    context: 'cancelReviewRequest',
  });
}
